"use client";

import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";

const products = [
  { href: "/story-builder", label: "Stories" },
  { href: "/anime-story-generator", label: "Anime" },
  { href: "/tv-show-story-builder", label: "Netflix Seasons" },
  { href: "/drama-story-generator", label: "Dramas" },
  { href: "/ai-poem-generator", label: "Poems" },
];

const navLinks = [
  { href: "/#pricing", label: "Pricing" },
  { href: "/blog", label: "Blog" },
  { href: "/stories", label: "Stories" },
];

export default function Navbar() {
  const [productsOpen, setProductsOpen] = useState(false);
  const [isDark, setIsDark] = useState(false);
  const productsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (productsRef.current && !productsRef.current.contains(e.target as Node)) {
        setProductsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Detect dark sections
  useEffect(() => {
    const darkSections = document.querySelectorAll("#features");
    if (!darkSections.length) return;
    const obs = new IntersectionObserver(
      (entries) => setIsDark(entries.some((e) => e.isIntersecting)),
      { threshold: 0.05 }
    );
    darkSections.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  return (
    <header className="sticky top-0 z-50 mt-3 w-full h-0 overflow-visible px-4 sm:px-6">
      <div className="mx-auto flex max-w-7xl items-center gap-3">

        {/* ── Floating glass island ── */}
        <div className={`relative flex flex-1 items-center rounded-2xl border px-5 py-3 backdrop-blur-xl transition-colors duration-500 ${
          isDark
            ? "border-white/15 bg-black/30 shadow-[0_8px_32px_rgba(0,0,0,0.4),inset_0_1px_0_rgba(255,255,255,0.08)]"
            : "border-white/50 bg-white/20 shadow-[0_8px_32px_rgba(0,0,0,0.1),inset_0_1px_0_rgba(255,255,255,0.6)]"
        }`}>

          {/* Glass inner top sheen */}
          <div className={`pointer-events-none absolute inset-0 rounded-2xl bg-gradient-to-b transition-opacity duration-500 ${
            isDark ? "from-white/8 via-white/3 to-transparent" : "from-white/40 via-white/10 to-transparent"
          }`} />

          {/* Iridescent top edge */}


          {/* Logo */}
          <Link href="/" className="relative z-10 shrink-0 mr-6" aria-label="Storly">
            <span className="text-[17px] font-black tracking-[0.2em]">
              <span className="bg-gradient-to-r from-blue-500 to-blue-600 bg-clip-text text-transparent">S</span>
              <span className="bg-gradient-to-r from-yellow-400 to-amber-500 bg-clip-text text-transparent">t</span>
              <span className="bg-gradient-to-r from-pink-500 to-rose-500 bg-clip-text text-transparent font-light">o</span>
              <span className="bg-gradient-to-r from-purple-500 to-violet-600 bg-clip-text text-transparent">r</span>
              <span className="bg-gradient-to-r from-emerald-400 to-green-500 bg-clip-text text-transparent">l</span>
              <span className="bg-gradient-to-r from-orange-500 to-red-500 bg-clip-text text-transparent">y</span>
            </span>
          </Link>

          {/* Divider */}
          <div className="relative z-10 hidden md:block w-px h-5 bg-gray-300/60 mr-5 shrink-0" />

          {/* Nav links */}
          <nav aria-label="Primary" className="relative z-10 hidden md:flex flex-1 items-center gap-0.5">
            <Link
              href="/#how-it-works"
              className={`rounded-xl px-3.5 py-2 text-sm font-medium transition-all duration-300 ${isDark ? "text-white/70 hover:bg-white/10 hover:text-white" : "text-gray-500 hover:bg-black/5 hover:text-gray-900"}`}
            >
              How It Works
            </Link>

            {/* Products dropdown — position 2 */}
            <div ref={productsRef} className="relative">
              <button
                onClick={() => setProductsOpen((o) => !o)}
                className={`flex items-center gap-1.5 rounded-xl px-3.5 py-2 text-sm font-medium transition-all duration-300 ${isDark ? "text-white/70 hover:bg-white/10 hover:text-white" : "text-gray-500 hover:bg-black/5 hover:text-gray-900"}`}
              >
                Products
                <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${productsOpen ? "rotate-180" : ""}`} />
              </button>

              {productsOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2.5 w-52 rounded-xl bg-white/90 backdrop-blur-2xl shadow-[0_8px_32px_rgba(0,0,0,0.12)] py-1.5 z-50 ring-1 ring-black/5">
                  <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 rotate-45 bg-white/90 ring-1 ring-black/5 ring-b-0 ring-r-0" />
                  {products.map((product) => (
                    <Link
                      key={product.href}
                      href={product.href}
                      onClick={() => setProductsOpen(false)}
                      className="flex items-center px-4 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50/80 transition-colors"
                    >
                      {product.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Remaining links */}
            {navLinks.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                className={`rounded-xl px-3.5 py-2 text-sm font-medium transition-all duration-300 ${isDark ? "text-white/70 hover:bg-white/10 hover:text-white" : "text-gray-500 hover:bg-black/5 hover:text-gray-900"}`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* ── Get Access CTA — inside island, right side ── */}
          <Link
            href="https://app.storly.world/register"
            target="_blank"
            rel="noreferrer"
            className="relative z-10 ml-auto hidden md:inline-flex items-center gap-2 shrink-0 rounded-xl bg-gradient-to-r from-purple-600 to-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:from-purple-700 hover:to-indigo-700 hover:shadow-md transition-all duration-200"
          >
            Get Access
          </Link>
        </div>

      </div>
    </header>
  );
}
